import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "../App.scss";
import userAPI from "../api/userAPI";
import Footer from "../components/Footer";
import MultipleEntryPassWidget from "../components/MultipleEntryPass/MultipleEntryPassWidget";
import MenRun from "../img/menrun.png";
import WomanPilates from "../img/womanpilates.png";
import ConfirmPopup from "../shared/confirmPopup";

import WOWMainPageCover from "../img/WOWMainPageCover.png";
import {
  canBuyNewMultipleEntryPassSelector,
  checkCanPurchasePass,
  fetchMultipleEntryPasses,
  hasActiveMultipleEntryPassSelector,
} from "../store/multipleEntryPassSlice";
import { selectPartner } from "../store/partnerStore";
import { updateIsHaveCreditCard } from "../store/userSlice";
import { PARTNER_KEYS, resolvePartnerSync } from "../utils/partner";
import { isV2Enabled, V2_SCREEN_KEYS } from "../v2/registry";
import HomeV2 from "../v2/screens/home/Home";

const MainPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userParams = useSelector((state) => state.userSlice.userData);
  const mainUserParams = useSelector((state) => state.userSlice.mainUserData);

  const hasActiveMultipleEntryPass = useSelector(hasActiveMultipleEntryPassSelector);

  const canBuyNewMultipleEntryPass = useSelector(canBuyNewMultipleEntryPassSelector);
  const { t } = useTranslation();
  // resolvePartnerSync, not the bare selector: this screen is the first paint of
  // "/" and "/home", and the store partner is still NONE on that frame (setPartner
  // lands in silentLogin's post-paint effect). The bare value sent frame 1 down
  // the legacy branch below — a visible WOW-styled flash before the V2 home.
  const partner = resolvePartnerSync(useSelector(selectPartner));
  const isClalit = partner === PARTNER_KEYS.CLALIT;
  const isOvdimBari = partner === PARTNER_KEYS.OVDIM_BARI;
  const isMaoflaamit = partner === PARTNER_KEYS.MAOFLAAMIT;
  const isLeumit = partner === PARTNER_KEYS.LEUMIT;

  const [showBookingPopup, setShowBookingPopup] = useState(false);

  const showWidgetBuyMultipleEntryPass = !hasActiveMultipleEntryPass && canBuyNewMultipleEntryPass;

  const getBookingClickCount = () => {
    return parseInt(localStorage.getItem("freefit_workout_booking_clicks") || "0");
  };

  const incrementBookingClickCount = () => {
    const currentCount = getBookingClickCount();
    localStorage.setItem("freefit_workout_booking_clicks", (currentCount + 1).toString());
  };

  const handleBookWorkoutClick = () => {
    if (isLeumit || isMaoflaamit) {
      navigate("/roomChoice");
      return;
    }
    const clickCount = getBookingClickCount();
    if (clickCount < 2) {
      setShowBookingPopup(true);
    } else {
      navigate("/roomChoice");
    }
  };

  const checkCreditCard = async () => {
    try {
      let isHaveCreditCard = await userAPI.isClientHaveCreditCard({
        token: mainUserParams.Token,
        id: mainUserParams.ID,
        phone: mainUserParams.Phone,
      });

      if (isHaveCreditCard.data.Error !== 0) {
        throw {
          code: isHaveCreditCard.data.Error,
          mess: isHaveCreditCard.data.Message,
        };
      }

      dispatch(updateIsHaveCreditCard(JSON.parse(isHaveCreditCard.data.Data).HasCreditCard));
    } catch (error) {
      console.error("Failed to check credit card:", error);
    }
  };

  const handlePopupConfirm = () => {
    incrementBookingClickCount();
    setShowBookingPopup(false);
    navigate("/roomChoice");
  };

  useEffect(() => {
    if ((isClalit || isOvdimBari || isMaoflaamit) && mainUserParams?.Token && mainUserParams?.ID && mainUserParams?.Phone) {
      const params = {
        token: mainUserParams.Token,
        id: mainUserParams.ID,
        phone: mainUserParams.Phone,
      };

      dispatch(fetchMultipleEntryPasses(params));
      dispatch(
        checkCanPurchasePass({
          token: mainUserParams.Token,
          id: mainUserParams.ID,
          phone: mainUserParams.Phone,
        }),
      );
    }

    // Token-guarded like the block above: the partner flags resolve synchronously
    // now, so without this the call fired before the handshake token landed —
    // a guaranteed-failing request on every entry.
    if ((isClalit || isOvdimBari || isMaoflaamit) && mainUserParams?.Token) checkCreditCard();
  }, [mainUserParams]);

  // V2 home page. The effects above still run for V2 partners — they populate
  // the passes and the credit-card flag that the V2 screens read from the store.
  if (isV2Enabled(V2_SCREEN_KEYS.HOME, partner)) return <HomeV2 />;

  return (
    <div className="main-page-container">
      <div className="main-page-wrapper">
        {isClalit || isOvdimBari || isMaoflaamit || isLeumit ? (
          <div className="main-page-imgs">
            <img src={MenRun} alt="Man running" />
            <img src={WomanPilates} alt="Woman doing pilates" />
          </div>
        ) : (
          <div className="main-page-imgs">
            <img src={WOWMainPageCover} alt="WOW Main Page Cover" />
          </div>
        )}
        <div className={isMaoflaamit ? "main-page-text-maof" : "main-page-text"}>
          {isClalit || isOvdimBari || isMaoflaamit  ? (
            <>
              <p>{t("HEY_FIRST_NAME", { firstName: userParams.FirstName })}</p>
              {showWidgetBuyMultipleEntryPass ? (
                <div>
                  <MultipleEntryPassWidget isNew={true} />
                </div>
              ) : (
                <p>{isMaoflaamit ? t("MORE_THAN_1000_GYMS_FOR_25_ILS_Maof") : t("MORE_THAN_1000_GYMS_FOR_25_ILS")}</p>
              )}
            </>
          ) : (
            <>
              <p>{t("HEY_FIRST_NAME", { firstName: userParams.FirstName })}</p>
              <p className={(isLeumit || isMaoflaamit) ? "text-dark" : ""}>{t("MORE_THAN_1000_GYMS_FOR_20_ILS")}</p>
            </>
          )}
        </div>

        <div className="main-page-btn-container-main">
          {(isClalit || isLeumit || isMaoflaamit) && (
            <button className="main-page-btn big-button" onClick={handleBookWorkoutClick}>
              {t("BOOK_A_WORKOUT")}
            </button>
          )}
          <button
            className={(isClalit || isLeumit || isMaoflaamit) ? "main-page-btn" : "main-page-btn big-button"}
            onClick={() => navigate("/fitExtendSelector")}
          >
            {t("WHERE_TO_GO")}
          </button>
          <button className="main-page-btn" onClick={() => navigate(`/orderHistory`)}>
            {t("MY_ORDERS")}
          </button>
          {(isClalit || isOvdimBari ||isMaoflaamit) && (
            <button className="main-page-btn" onClick={() => navigate(`/multipleEntryPass`)}>
              {t("MY_MULTIPLE_ENTRY_PASS")}
            </button>
          )}
        </div>
      </div>

      {isClalit && (
        <ConfirmPopup
          isOpen={showBookingPopup}
          setIsOpen={setShowBookingPopup}
          headerTextTop={t("NEW_UPDATES")}
          bodySubText={
            <div style={{ fontSize: "17px", lineHeight: "26px" }}>
              <div>{t("NEW_UPDATE_SUBTITLE")}</div>
              <div style={{ marginTop: "10px" }}>{t("NEW_UPDATE_OPTION_1-1")}</div>
              <div style={{ marginRight: "10px" }}>{t("NEW_UPDATE_OPTION_1-2")}</div>
              <div>{t("NEW_UPDATE_OPTION_2")}</div>
            </div>
          }
          btnText={t("CONFIRM")}
          handleClose={handlePopupConfirm}
        />
      )}

      <Footer />
    </div>
  );
};

export default MainPage;
